import NotificationTypes from '@/enums/NotificationTypes';

export default class Alert {

    id = null;
    notification_id = null;
    notification = null;
    status = 'unsent';
    read_at = null
    created_at = null;
    updated_at = null;
    logs = [];
    diffs = [];

    constructor(data) {
        this.id = data?.id || this.id;
        this.notification_id = data?.notification_id || this.notification_id;
        this.notification = data?.notification || this.notification;
        this.status = data?.status || this.status;
        this.read_at = data?.read_at || this.read_at;
        this.created_at = data?.created_at || this.created_at;
        this.updated_at = data?.updated_at || this.updated_at;
        this.logs = data?.logs || this.logs;
        this.diffs = data?.diffs || this.diffs;
        
        return this;
    }

    get log() {
        return this?.logs[0] ?? {};
    }

    get isRead() {
        return this.read_at != null
    }

    get userName() {
        return this.log?.user?.name ? this.log.user.name : "System Process"
    }

    get headline() {
        if(!this?.log ?? false) return "";

        const count = this.logs.length;
        let name = this?.log.model?.meta?.info?.name ?? this.log?.model?.kova?.Name ?? null;
        const modelType = this.ucFirst(NotificationTypes.getLabel(this.log?.model_type) ?? "(deleted item)");
        let was = count > 1 ? 'were' : 'was';
        let modelName = this?.log?.model?.model?.meta?.info?.name ?? this?.log?.model?.model?.kova?.Name ?? this?.log?.model?.collection?.meta?.info?.name ?? this?.log?.model?.collection?.kova?.Name ?? (this?.log?.model?.model?.address && this.log.model.model.address[0]) ?? "(deleted item)";
        let to = this?.log?.action === 'DELETED' ? 'from' : (this.log.action === 'UPDATED' ? 'on' : 'to');
        let context = ['attachment', 'agent_collection'].includes(this.log.model_type) ? ` ${to} ${modelName}` : '';

        switch (this.log.model_type) {
            case 'agent_collection' :
                name = `${count} sales team ${count > 1 ? 'members' : 'member'}`
                break;
            case 'attachment' :
                name = `${count} ${count > 1 ? 'images' : 'image'}`
                break;
            case 'sales_order' :
                name = this?.log?.model?.address[0] ?? null;
                break;
        }

        return `${name || modelType} ${was} ${this.action(this?.log?.action)}${context} by ${this.userName}`;
    }

    get contents() {
        let array = []
        this.logs.forEach(log => {
            if(log.action === 'UPDATED') {
                array.push(`${log.model.media?.filename} order was set to ${log.differences['meta:order'] ?? 'undefined'}`)
            } else {
                array.push(log.model.media?.filename ?? log.model?.agent.name);
            }
        });
        return array;
    }

    get icon() {
        if((this?.logs[0]?.user_id ?? null) === null) 
            return 'fa-monitor';
        switch(this?.logs[0]?.model_type) {
            case 'collection' :
            case 'floorplan' :
                return `fa-${this.log.model_type}`
            case 'division' :
                return `fa-community`
            case `agent_collection` :
                return `fa-sales-team`
            case `attachment` :
                return 'fa-photo'
            case 'sales_order' :
            default :
                return 'fa-home-available';
        }
    }

    get notificationIcon() {
        switch(this?.logs[0]?.model_type) {
            default :
                return '/favicon.svg';
        }
    }

    get url() {
        if(!this.log?.model || (this.log?.model?.deleted_at ?? null) !== null || (this.log?.model?.model?.deleted_at ?? null) !== null) return false;
        let name,
        id = this.log.model_id,
        tab = false;
        switch(this.log.model_type) {
            case 'agent_collection' :
                name = 'collection-tab';
                id = this.log.model.collection.id;
                tab = 'agents';
                break;
            case 'attachment' :
                switch(this.log?.model?.item_type) {
                case 'division' :   
                    name = 'community-tab'
                    break;
                case 'sales_order' :
                    name = 'available-homes-tab';
                    break;     
                default :
                    name = `${this.log?.model?.item_type ?? 'floorplan'}-show`;
                    break;
                }
                id = this.log.model?.item_id;
                tab = 'photos';
                break;
            case 'collection' :
            case 'floorplan' :
                name = `${this.log.model_type}-show`
                break;
            case 'division' :
                name = 'community-show';
                break;
            case 'sales_order' :
                name = 'available-homes-show';
                break;
            default:
                return false;
        }
        return tab ? {name, params: {id, tab}} : {name, params: {id}};
    }

    get date() {
        if (!this.created_at) return "";
        
        const created_at = new Date(this.created_at);

        let date = new Intl.DateTimeFormat('en-US', {
            month: 'long',
            day: 'numeric',
            ...(created_at.getFullYear() === new Date().getFullYear() ? {} : { year: 'numeric' })
        }).format(created_at);

        let time = new Intl.DateTimeFormat('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
            timeZoneName: 'short',
        }).format(created_at); 
        
        return `${date} at ${time}`;
    }

    get since() {
        if(!this.created_at) return 'never';

        const created_at = new Date(this.created_at) / 1000;
        const now = new Date() / 1000;
        const diff = Math.round(now - created_at);
        let time = diff;
        let unit = 'sec'

        if(diff > 60 && diff < 3600) {
            unit = 'min';
            time = Math.round(diff / 60);
        } else if(diff > 3600 && diff < 86400) {
            unit = diff < 7200 ? 'hr' : 'hrs';
            time = Math.round(diff / 3600);
        } else if(diff > 86400 && diff < 2592000) {
            unit = diff < 172800 ? 'day' : 'days';
            time = Math.round(diff / 86400);
        } else if(diff > 2592000 && diff < 31536000) {
            unit = diff < 5184000 ? 'month' : 'months';
            time = Math.round(diff / 259200);
        } else if(diff > 31536000) {
            unit = diff < 63072000 ? 'year' : 'years';
            time = Math.round(diff / 31536000);
        }

        return `${time} ${unit} ago`

    }

    get details() {
        return Object.keys(this.diffs ?? []).length > 0
    }
 
    ucFirst(value) {
        return `${(value[0] ?? '').toUpperCase()}${(value ?? []).slice(1)}`; 
    }

    action(value) {
        switch(value) {
            case 'CREATED' :
                return 'added';
            default :
                return value?.toLowerCase() ?? ""
        }
    }
}